import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; 
import { useAuth } from '../../hooks/useAuth';
import { ShieldCheck, RotateCcw, CheckCircle, XCircle } from 'lucide-react';
import './VerifyPhone.css';

const VerifyPhone = () => {
  const [codes, setCodes] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(''); 
  const inputRefs = useRef([]);
  const { verifyOtp, resendOtp, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // Added

  // Get phone number from location state or user or localStorage
  useEffect(() => {
    const phoneFromState = location.state?.phoneNumber;
    const phoneFromUser = user?.phoneNumber;
    const phoneFromStorage = localStorage.getItem('wa_phone');
    const phone = phoneFromState || phoneFromUser || phoneFromStorage || '';
    setPhoneNumber(phone);
  }, [location.state, user]);

  useEffect(() => {
    if (countdown <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCodes = [...codes];
    newCodes[index] = value.slice(-1);
    setCodes(newCodes);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCodes.every(c => c !== '') && value) {
      handleSubmit(newCodes.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codes[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCodes = pasted.split('');
      setCodes(newCodes);
      inputRefs.current[5]?.focus();
      handleSubmit(pasted);
    }
  };

  const handleSubmit = async (codeStr) => {
    const finalCode = codeStr || codes.join('');
    if (finalCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await verifyOtp(finalCode);
      setSuccess('Phone verified successfully! Redirecting...');
      
      // Get the phone number to pass to payment page
      const phoneToPass = phoneNumber || localStorage.getItem('wa_phone') || '';
      
      setTimeout(() => {
        navigate('/registration-payment', { 
          state: { 
            phoneNumber: phoneToPass,
            fromVerification: true 
          } 
        });
      }, 1500);
      
    } catch (err) {
      const msg = err.response?.data?.error || 'Invalid or expired code';
      setError(msg);
      setCodes(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setResendLoading(true);
    setError('');
    setSuccess('');
    try {
      await resendOtp();
      setSuccess('New code sent to your phone!');
      setCountdown(60);
      setCanResend(false);
      setCodes(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setResendLoading(false);
    }
  };

  // Display phone with masking
  const getDisplayPhone = () => {
    const p = phoneNumber || localStorage.getItem('wa_phone') || '';
    if (p.length >= 10) {
      return p.substring(0, 5) + '****' + p.slice(-2);
    }
    return 'your phone';
  };

  return (
    <div className="verify-container">
      <div className="verify-bg">
        <div className="verify-bg-gradient"></div>
      </div>

      <div className="verify-card">
        <div className="verify-icon">
          <ShieldCheck size={32} />
        </div>

        <h2 className="verify-title">Verify Your Phone</h2>
        <p className="verify-subtitle">
          We sent a 6-digit code to <strong>{getDisplayPhone()}</strong>
        </p>

        {success && (
          <div className="vp-alert-success">
            <CheckCircle size={16} />
            <span>{success}</span>
          </div>
        )}

        {error && (
          <div className="vp-alert-error">
            <XCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="otp-inputs" onPaste={handlePaste}>
          {codes.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              className={`otp-input ${digit ? 'filled' : ''} ${error ? 'otp-error' : ''}`}
              value={digit}
              onChange={e => handleChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              disabled={loading}
            />
          ))}
        </div>

        <button
          className="btn-verify"
          onClick={() => handleSubmit()}
          disabled={loading || codes.some(c => c === '')}
        >
          {loading ? (
            <span className="btn-loading">
              <span className="spinner-small"></span>
              Verifying...
            </span>
          ) : (
            <span>Verify Phone Number</span>
          )}
        </button>

        <div className="resend-section">
          {canResend ? (
            <button
              className="resend-btn"
              onClick={handleResend}
              disabled={resendLoading}
            >
              <RotateCcw size={15} />
              {resendLoading ? 'Sending...' : 'Resend Code'}
            </button>
          ) : (
            <p className="resend-timer">
              Resend code in <strong>{countdown}s</strong>
            </p>
          )}
        </div>

        <p className="verify-hint">
          Wrong number? <a href="/register">Go back to register</a>
        </p>
      </div>
    </div>
  );
};

export default VerifyPhone;